import { CHANNELS } from '@kinohelpdesk/shared';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle,
  Clock,
  FileText,
  Globe,
  type LucideIcon,
  Smartphone,
  Target,
  Tv,
  XCircle,
} from 'lucide-react';
import { statsApi } from '@/api/client';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';

const CHANNEL_ICONS: Record<string, LucideIcon> = {
  ios: Smartphone,
  tvos: Tv,
  service: Globe,
};

export function OverviewPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: () => statsApi.get(),
  });

  if (isLoading) {
    return (
      <Layout title="Обзор">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Обзор" subtitle="Статистика и показатели">
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard
          title="Всего заявок"
          value={stats?.overview.total ?? 0}
          icon={FileText}
          color="cyan"
        />
        <StatCard
          title="Ожидают"
          value={stats?.overview.pending ?? 0}
          icon={Clock}
          color="yellow"
        />
        <StatCard
          title="Одобрено"
          value={stats?.overview.approved ?? 0}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Отклонено"
          value={stats?.overview.rejected ?? 0}
          icon={XCircle}
          color="red"
        />
        <StatCard
          title="Решено сегодня"
          value={stats?.overview.resolved_today ?? 0}
          icon={Target}
          color="magenta"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Channel */}
        <div className="cyber-card p-4">
          <h3 className="text-sm font-display text-white mb-3">По платформам</h3>
          <div className="space-y-2">
            {stats?.by_channel.map((item) => {
              const channel = CHANNELS[item.channel as keyof typeof CHANNELS];
              const ChannelIcon = CHANNEL_ICONS[item.channel] ?? Globe;
              return (
                <div key={item.channel} className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-300">
                    <ChannelIcon className="w-3.5 h-3.5 text-gray-400" />
                    {channel?.label ?? item.channel}
                  </span>
                  <span className="text-sm text-neon-cyan font-mono">{item.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Submitters */}
        <div className="cyber-card p-4">
          <h3 className="text-sm font-display text-white mb-3">Топ пользователей</h3>
          <div className="space-y-2">
            {stats?.top_submitters.slice(0, 5).map((item, i) => (
              <div key={item.username} className="flex items-center justify-between">
                <span className="text-sm text-gray-300">
                  <span className="text-neon-magenta mr-2">#{i + 1}</span>@{item.username}
                </span>
                <span className="text-sm text-neon-cyan font-mono">{item.count}</span>
              </div>
            ))}
            {(!stats?.top_submitters || stats.top_submitters.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">Нет данных</p>
            )}
          </div>
        </div>
      </div>

      {/* Daily chart placeholder */}
      <div className="cyber-card p-4 mt-4">
        <h3 className="text-sm font-display text-white mb-3">Заявки за неделю</h3>
        <div className="h-48 flex items-end justify-between gap-2">
          {stats?.daily.slice(-7).map((item) => {
            const maxCount = Math.max(...(stats?.daily.map((d) => d.count) ?? [1]));
            const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
            return (
              <div key={item.date} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-neon-cyan/20 rounded-t transition-all duration-300 hover:bg-neon-cyan/40"
                  style={{ height: `${Math.max(height, 5)}%` }}
                />
                <span className="text-xs text-gray-500 mt-2">
                  {new Date(item.date).toLocaleDateString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                  })}
                </span>
              </div>
            );
          })}
          {(!stats?.daily || stats.daily.length === 0) && (
            <div className="flex-1 text-center text-gray-500 py-16">Нет данных</div>
          )}
        </div>
      </div>
    </Layout>
  );
}

interface StatCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  color: 'cyan' | 'yellow' | 'green' | 'red' | 'magenta';
}

function StatCard({ title, value, icon: Icon, color }: StatCardProps) {
  const colorClasses = {
    cyan: 'text-neon-cyan border-neon-cyan/30 bg-neon-cyan/5',
    yellow: 'text-neon-yellow border-neon-yellow/30 bg-neon-yellow/5',
    green: 'text-neon-green border-neon-green/30 bg-neon-green/5',
    red: 'text-neon-red border-neon-red/30 bg-neon-red/5',
    magenta: 'text-neon-magenta border-neon-magenta/30 bg-neon-magenta/5',
  };

  return (
    <div className={`cyber-card p-3 border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between mb-1">
        <Icon className="w-5 h-5 text-gray-400" />
        <span className={`text-2xl font-mono font-bold ${colorClasses[color].split(' ')[0]}`}>
          {value}
        </span>
      </div>
      <p className="text-xs text-gray-400">{title}</p>
    </div>
  );
}
